import { YELLOW } from '../Theme/palette';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { L } from '@lib';
import { OrderStatus } from '@models';
import { Modal, Popconfirm } from 'antd';
import React, { createContext, useContext } from 'react';

/**
 * A job that came from an estimate and has been approved (it is Booked now, or further on) has a
 * price the customer agreed to. Adding work or extras to it is allowed - things get forgotten, all
 * the time - but it changes that price, so each time the user is reminded and asked to confirm.
 * If they are not sure, the Estimates section's Revise sends the customer a new estimate instead.
 */
export const isApprovedEstimateJob = (order?: { isApproval?: boolean; status?: string } | null) =>
  !!order?.isApproval && order.status !== OrderStatus.Approval;

// Provided by the hosts of a saved job (the job card, a build's job editor). Anywhere else - a new
// booking, the revise drawer - there is no approved price to protect, so the default is "no".
const ApprovedEstimateContext = createContext(false);
export const ApprovedEstimateProvider = ApprovedEstimateContext.Provider;
export const useApprovedEstimate = () => useContext(ApprovedEstimateContext);

const TITLE = L('This is an approved estimate job');
const TEXT = L(
  'The customer has already approved the price of this job. Adding work or extras changes it. If you are not sure about the change, use Revise in the Estimates section so the customer can approve the new price.'
);

/**
 * Asks before `onConfirm` runs when the job is an approved estimate; runs it straight away
 * otherwise. The button is handed over as a function so the right click handler lands on it:
 * the plain one when there is nothing to confirm, none at all when a Popconfirm owns the click.
 */
export const ApprovedEstimateConfirm: React.FC<{
  onConfirm?: () => void;
  children: (trigger: { onClick?: () => void }) => React.ReactElement;
}> = ({ onConfirm, children }) => {
  const approved = useApprovedEstimate();

  if (!approved) return children({ onClick: onConfirm });

  return (
    <Popconfirm
      title={<span style={{ fontWeight: 600 }}>{TITLE}</span>}
      description={<div style={{ maxWidth: 300 }}>{TEXT}</div>}
      okText={L('Continue')}
      cancelText={L('Cancel')}
      icon={<ExclamationCircleFilled style={{ color: YELLOW.badge }} />}
      onConfirm={onConfirm}
    >
      {children({})}
    </Popconfirm>
  );
};

/** The same question for a choice made inside a popover, where a Popconfirm cannot anchor. */
export const confirmApprovedEstimate = (onOk: () => void) =>
  Modal.confirm({
    title: TITLE,
    content: TEXT,
    icon: <ExclamationCircleFilled style={{ color: YELLOW.badge }} />,
    okText: L('Continue'),
    cancelText: L('Cancel'),
    onOk,
  });
